/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase boundary; new tables not yet in generated types. See mem://preferences/lint-debt.md */
import { supabase } from "@/integrations/supabase/client";

export type PublishType = {
  id: string;
  workspace_id: string;
  key: string;
  label: string;
  is_seed: boolean;
  position: number;
  created_at: string;
  updated_at: string;
};

const TBL = "workspace_publish_types" as const;
const TBL_M2M = "workspace_corpus_document_publish_types" as const;

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "type";

export async function listPublishTypes(workspaceId: string): Promise<PublishType[]> {
  const { data, error } = await (supabase as any)
    .from(TBL)
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("position", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as PublishType[];
}

export async function createPublishType(input: {
  workspaceId: string;
  label: string;
  position?: number;
}): Promise<PublishType> {
  const key = slugify(input.label);
  const { data, error } = await (supabase as any)
    .from(TBL)
    .insert({
      workspace_id: input.workspaceId,
      label: input.label.trim() || "Untitled",
      key,
      is_seed: false,
      position: input.position ?? 100,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as PublishType;
}

export async function renamePublishType(id: string, label: string): Promise<void> {
  const { error } = await (supabase as any)
    .from(TBL)
    .update({ label: label.trim() || "Untitled" })
    .eq("id", id);
  if (error) throw error;
}

export async function deletePublishType(id: string): Promise<void> {
  const { error } = await (supabase as any).from(TBL).delete().eq("id", id);
  if (error) throw error;
}

// ---------- m2m helpers (corpus doc <-> publish type) ----------
//
// Two distinct concepts share this m2m table:
//   - Publish type tag = "this doc IS an Article / Newsletter / etc." (metadata)
//   - is_style_sample  = "use this doc as a Writing Style Sample for that publish type"
//                        (drives AI grounding retrieval; strict — no silent fallback)

export type DocumentPublishTypeTag = {
  publishTypeId: string;
  isStyleSample: boolean;
};

/**
 * True when the workspace has at least one Corpus doc *starred as a style
 * sample* for some publish type. Used by the Corpus sidebar empty-state hint.
 */
export async function hasAnyTaggedDocument(workspaceId: string): Promise<boolean> {
  const { count, error } = await (supabase as any)
    .from(TBL_M2M)
    .select("document_id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId)
    .eq("is_style_sample", true);
  if (error) throw error;
  return (count ?? 0) > 0;
}

export async function listDocumentPublishTypeTags(
  documentId: string,
): Promise<DocumentPublishTypeTag[]> {
  const { data, error } = await (supabase as any)
    .from(TBL_M2M)
    .select("publish_type_id, is_style_sample")
    .eq("document_id", documentId);
  if (error) throw error;
  return ((data ?? []) as Array<{ publish_type_id: string; is_style_sample: boolean }>).map(
    (r) => ({ publishTypeId: r.publish_type_id, isStyleSample: !!r.is_style_sample }),
  );
}

/**
 * Replace the set of publish-type tags on a document.
 * - New tags are inserted with `is_style_sample = false` (metadata-only).
 * - Existing tags keep their current `is_style_sample` flag.
 * - Removed tags are deleted.
 */
export async function setDocumentPublishTypes(input: {
  documentId: string;
  workspaceId: string;
  publishTypeIds: string[];
}): Promise<void> {
  const current = await listDocumentPublishTypeTags(input.documentId);
  const currentIds = new Set(current.map((r) => r.publishTypeId));
  const desired = new Set(input.publishTypeIds);

  const toDelete = current
    .filter((r) => !desired.has(r.publishTypeId))
    .map((r) => r.publishTypeId);
  const toInsert = input.publishTypeIds.filter((id) => !currentIds.has(id));

  if (toDelete.length > 0) {
    const { error: delErr } = await (supabase as any)
      .from(TBL_M2M)
      .delete()
      .eq("document_id", input.documentId)
      .in("publish_type_id", toDelete);
    if (delErr) throw delErr;
  }
  if (toInsert.length > 0) {
    const rows = toInsert.map((pid) => ({
      document_id: input.documentId,
      publish_type_id: pid,
      workspace_id: input.workspaceId,
      is_style_sample: false,
    }));
    const { error: insErr } = await (supabase as any).from(TBL_M2M).insert(rows);
    if (insErr) throw insErr;
  }
}

/**
 * Toggle the "Writing Style Sample" flag on a single (doc, publish type) tag.
 * Caller must ensure the tag already exists.
 */
export async function setDocumentPublishTypeStyleSample(input: {
  documentId: string;
  publishTypeId: string;
  isStyleSample: boolean;
}): Promise<void> {
  const { error } = await (supabase as any)
    .from(TBL_M2M)
    .update({ is_style_sample: input.isStyleSample })
    .eq("document_id", input.documentId)
    .eq("publish_type_id", input.publishTypeId);
  if (error) throw error;
}
